import { type FunctionComponent, useContext } from 'react';
import { Box } from '@atlaskit/primitives';
import Footer from '../Footer/Footer';
import MainContent from '../MainContent/MainContent';
import McpConsentLayout from '../MainContent/MCP-optional-sites/McpConsentLayout';
import TwgConsentLayout from '../MainContent/TWG/TwgConsentLayout';
import RovoConsentLayout from '../MainContent/Rovo/RovoConsentLayout';
import ConsentForm from './ConsentForm';
import {
  ReactConsentContext,
  narrowConsentContentStyle,
  useToken,
} from './ConsentContextProvider';

// Re-exported for back-compat with existing importers (e.g. VR fixtures).
export { narrowConsentContentStyle };

/**
 * Renders consent chrome: `<main>` is always the direct child of page `Content`
 * (same as pre-MCP-redesign), then `<form>`, so the layouts that go through
 * MainContent are unchanged.
 *
 * MCP, TWG and Rovo return ahead of that chrome and own their whole layout —
 * their own form and footer, and for MCP its own context providers too — as
 * their styling differs greatly from the other consent flows. Every other
 * service type is dispatched by MainContent inside the shared chrome.
 */
const ConsentLayout: FunctionComponent = () => {
  const { serviceType } = useToken();
  const consentContext = useContext(ReactConsentContext);

  if (serviceType === 'MCP') {
    return <McpConsentLayout />;
  }

  if (serviceType === 'TWG') {
    return <TwgConsentLayout />;
  }

  if (serviceType === 'Rovo') {
    return <RovoConsentLayout />;
  }

  return (
    <Box as="main" xcss={narrowConsentContentStyle}>
      <ConsentForm action={serviceType === 'DAG' ? '/oauth/activate' : undefined}>
        <MainContent />
        <Footer acceptDisabled={consentContext?.acceptDisabled} />
      </ConsentForm>
    </Box>
  );
};

export default ConsentLayout;
